import {
  ArrowDropDown,
  CloseOutlined,
  EventNote,
  FilterAltOutlined,
} from "@mui/icons-material";
import {
  alpha,
  Box,
  Button,
  Grid2,
  Paper,
  Popover,
  Typography,
  useTheme,
} from "@mui/material";
import { DateCalendar, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import * as dayjs from "dayjs";
import { useState } from "react";

type FilterItemProps = {
  title: string;
  value: any;
  setValue: (value: any) => void;
  valueToInitial?: any;
};

const FilterItem = ({
  title,
  value,
  setValue,
  valueToInitial,
}: FilterItemProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const theme = useTheme();
  const handleClose = () => {
    setAnchorEl(null);
  };
  const handleClick = (event: React.SyntheticEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const styles = {
    text: {
      // color: theme.palette.text.secondary,
      fontSize: "14px",
      fontWeight: 500,
      height: "20px",
    },
    paperBrick: {
      flexDirection: "row",
      justifyContent: "center",
      alignItems: "center",
      paddingLeft: 1.5,
      gap: 0.5,
      "&:hover": {
        cursor: "pointer",
        backgroundColor: alpha(theme.palette.background.paper, 0.7),
      },
    },
    size: { fontSize: 20 },
    popover: {
      "& .MuiPopover-paper": {
        // width: "300px",
        // height: "300px",
        boxShadow:
          theme.palette.mode === "dark"
            ? "0px 4px 14px 0px rgba(0, 0, 0, 0.5)"
            : "0px 4px 14px 0px rgba(16, 24, 40, 0.12)",
      },
    },
  };

  return (
    <>
      <Paper
        variant="brick"
        sx={styles.paperBrick}
        onClick={handleClick}
        role="button"
        tabIndex={0}
        aria-haspopup="true"
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleClick(e);
          }
        }}
      >
        <EventNote sx={styles.size} />
        <Typography sx={styles.text}>{title}</Typography>
        {!!value && (
          <Typography sx={styles.text}>
            {": " + dayjs(value).toDate().toLocaleDateString()}
          </Typography>
        )}
        <ArrowDropDown sx={styles.size} />
      </Paper>
      <Popover
        id={"modal-data"}
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        sx={styles.popover}
      >
        <LocalizationProvider
          dateAdapter={AdapterDayjs}
          adapterLocale={"pt-br"}
        >
          <DateCalendar
            value={value}
            onChange={(newValue) => setValue(newValue)}
            minDate={valueToInitial ? valueToInitial : null}
          />
        </LocalizationProvider>
      </Popover>
    </>
  );
};
type ReportFiltersProps = {
  filtroDataInicial: any;
  setFiltroDataInicial: (value: any) => void;
  filtroDataFinal: any;
  setFiltroDataFinal: (value: any) => void;
  filtroGeradoEm?: any;
  setFiltroGeradoEm?: (value: any) => void;
};

export default function ReportFilters({
  filtroDataInicial,
  setFiltroDataInicial,
  filtroDataFinal,
  setFiltroDataFinal,
  filtroGeradoEm,
  setFiltroGeradoEm,
}: ReportFiltersProps) {
  const theme = useTheme();

  const styles = {
    backgroundBox: {
      display: "flex",
      flexDirection: { xs: "column", sm: "row" },
      backgroundColor: theme.palette.background.textField,
      borderRadius: "5px",
      gap: 1.5,
      padding: "4px 4px 4px 8px",
      justifyContent: { xs: "start", sm: "center" },
      alignItems: { xs: "start", sm: "center" },
    },
    boxTitle: {
      display: "flex",
      flexDirection: "row",
      gap: 0.5,
    },
    text: {
      // color: theme.palette.text.secondary,
      fontSize: "14px",
      fontWeight: 500,
      height: "20px",
    },
    paperBrick: {
      flexDirection: "row",
      justifyContent: "center",
      alignItems: "center",
      paddingLeft: 1.5,
      width: "100%",
      gap: 0.5,
      "&:hover": {
        cursor: "pointer",
        backgroundColor: alpha(theme.palette.background.paper, 0.7),
      },
    },
    size: { fontSize: 20 },
  };
  const handleClearFilters = () => {
    setFiltroDataInicial(null);
    setFiltroDataFinal(null);
    if (setFiltroGeradoEm) {
      setFiltroGeradoEm(null);
    }
  };
  const conditionalClearFilter =
    filtroDataInicial || filtroDataFinal || filtroGeradoEm;
  return (
    <Box
      sx={{
        display: "flex",
        width: "fit-content",
        justifyContent: "space-between",
      }}
    >
      <Box sx={styles.backgroundBox}>
        <Grid2
          container
          rowSpacing={1}
          spacing={1}
          sx={{ alignItems: "center" }}
        >
          <Grid2 size={"auto"}>
            <Box sx={styles.boxTitle}>
              <FilterAltOutlined sx={styles.size} />
              <Typography sx={styles.text}>{"Filtros:"}</Typography>
            </Box>
          </Grid2>
          <Grid2 size={"auto"}>
            <FilterItem
              title="Data Inicial"
              value={filtroDataInicial}
              setValue={setFiltroDataInicial}
            />
          </Grid2>
          <Grid2 size={"auto"}>
            <FilterItem
              title="Data Final"
              value={filtroDataFinal}
              setValue={setFiltroDataFinal}
              valueToInitial={filtroDataInicial}
            />
          </Grid2>
          <Grid2 size={"auto"}>
            {setFiltroGeradoEm && (
              <FilterItem
                title="Gerado Em"
                value={filtroGeradoEm}
                setValue={setFiltroGeradoEm}
              />
            )}
          </Grid2>
          <Grid2 size={"auto"}>
            {conditionalClearFilter && (
              <Button
                variant="text"
                onClick={handleClearFilters}
                sx={{
                  height: "32px",
                  color: theme.palette.text.primary,
                }}
                startIcon={<CloseOutlined />}
              >
                {"Limpar Filtros"}
              </Button>
            )}
          </Grid2>
        </Grid2>
      </Box>
    </Box>
  );
}
